// Política de prazos do rito (SLA SOFT) — alerta visual, NÃO bloqueia transição.
//
// Espelha o padrão de retention-config.ts: constantes nomeadas em dias + função
// pura. ZERO persistência: o status é DERIVADO em runtime de um anchor (epoch-ms)
// que já existe no DenunciaState ou é derivado do audit (como `prorrogada`).
// ZERO mudança na state-machine.
//
// Conformidade (2026-07-14 — Divergência 3, POL-CMP-001 §7 / IT-CMP-002 §6):
//   - contagem em DIAS ÚTEIS (pula sábado/domingo; feriados = ponto de extensão
//     `feriados`, implementado como conjunto de datas 'YYYY-MM-DD' UTC — vazio
//     nesta fatia, follow-up explícito popular a lista);
//   - 5 janelas: Triagem 5 · Análise prévia 10 · Apuração 30 (+30) · Conclusão 5
//     · Retorno 5 — todas em dias ÚTEIS.
//   - Análise prévia e Conclusão são SUB-JANELAS dentro dos estados existentes
//     (Divergência 5) — NÃO há estado novo na state-machine; o SLA é soft.
//
// Limiar "vencendo" = 20% do prazo (piso 1 dia), proporcional em dias corridos —
// é só o gatilho visual de alerta, não a data-limite (essa é em dias úteis).

import type { DenunciaState } from './state-machine.js';

const UM_DIA_MS = 24 * 60 * 60 * 1000;

/** Triagem (recebimento → classificação): 5 dias úteis. */
export const PRAZO_TRIAGEM_DIAS = 5;

/** Análise prévia (classificação → abertura da instrução): 10 dias úteis. */
export const PRAZO_ANALISE_PREVIA_DIAS = 10;

/** Apuração/investigação: 30 dias úteis a partir da classificação da triagem. */
export const PRAZO_INVESTIGACAO_DIAS = 30;

/** Prorrogação da apuração: +30 dias úteis (total 60) — ação manual com rastro. */
export const PRAZO_INVESTIGACAO_PRORROGACAO_DIAS = 30;

/** Conclusão (apuração encerrada → decisão emitida): 5 dias úteis. */
export const PRAZO_CONCLUSAO_DIAS = 5;

/** Retorno ao denunciante (decisão emitida → retorno): 5 dias úteis. */
export const PRAZO_RETORNO_DIAS = 5;

/** Fração do prazo em que o status vira "vencendo" (piso de 1 dia). */
const LIMIAR_VENCENDO_FRACAO = 0.2;

/** Conjunto de feriados (datas 'YYYY-MM-DD' em UTC) a pular além do fim de semana.
 *  Ponto de extensão: vazio nesta fatia; popular como follow-up (calendário
 *  nacional/estadual). `addDiasUteis` já os respeita quando não-vazio. */
export type Feriados = ReadonlySet<string>;
const SEM_FERIADOS: Feriados = new Set<string>();

/** Chave de dia em UTC ('YYYY-MM-DD') — usada para casar contra `feriados`. */
function diaUtcKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Dia útil? Não é sábado (6) nem domingo (0) nem feriado (em UTC). */
function isDiaUtil(ms: number, feriados: Feriados): boolean {
  const dow = new Date(ms).getUTCDay();
  return dow !== 0 && dow !== 6 && !feriados.has(diaUtcKey(ms));
}

/**
 * Soma `dias` DIAS ÚTEIS a partir de `startMs`, pulando fim de semana (e
 * `feriados`, quando fornecidos). Preserva o horário do anchor (avança blocos de
 * 24h). Ex.: sexta + 3 úteis = quarta (pula sáb+dom). `dias <= 0` → devolve o
 * próprio `startMs` (sem avanço).
 */
export function addDiasUteis(
  startMs: number,
  dias: number,
  feriados: Feriados = SEM_FERIADOS,
): number {
  if (dias <= 0) return startMs;
  let cursor = startMs;
  let restantes = dias;
  while (restantes > 0) {
    cursor += UM_DIA_MS;
    if (isDiaUtil(cursor, feriados)) restantes -= 1;
  }
  return cursor;
}

export type PrazoStatus = 'no_prazo' | 'vencendo' | 'vencido';

/** Qual janela de SLA está em curso (para rótulo/telemetria; UI hoje é genérica). */
export type PrazoFase = 'triagem' | 'analise_previa' | 'apuracao' | 'conclusao' | 'retorno';

export interface PrazoResultado {
  readonly fase: PrazoFase;
  readonly status: PrazoStatus;
  /** Dias corridos até a data-limite (positivo = restantes, negativo = excedidos).
   *  A data-limite em si é contada em dias ÚTEIS; este número é "quantos dias até
   *  aquela data" para o texto do badge. */
  readonly diasRestantes: number;
  readonly prazoFimEpochMs: number;
}

/**
 * Função PURA: dado um anchor (epoch-ms de início da contagem), o prazo em dias
 * ÚTEIS, a fase e o `now`, retorna o status. `now` é injetado (determinístico,
 * testável). A data-limite é `addDiasUteis(anchor, prazoDias)`.
 */
export function avaliarPrazo(
  anchorEpochMs: number,
  prazoDias: number,
  now: number,
  fase: PrazoFase,
  feriados: Feriados = SEM_FERIADOS,
): PrazoResultado {
  const prazoFimEpochMs = addDiasUteis(anchorEpochMs, prazoDias, feriados);
  const msRestantes = prazoFimEpochMs - now;
  const limiarMs = Math.max(UM_DIA_MS, prazoDias * LIMIAR_VENCENDO_FRACAO * UM_DIA_MS);

  let status: PrazoStatus;
  if (msRestantes <= 0) {
    status = 'vencido';
  } else if (msRestantes <= limiarMs) {
    status = 'vencendo';
  } else {
    status = 'no_prazo';
  }

  const diasRestantes =
    msRestantes >= 0 ? Math.ceil(msRestantes / UM_DIA_MS) : Math.floor(msRestantes / UM_DIA_MS);

  return { fase, status, diasRestantes, prazoFimEpochMs };
}

/** Estados da APURAÇÃO (anchor classifiedAt, 30/60 úteis) — até o close_oitivas. */
const ESTADOS_APURACAO: ReadonlySet<string> = new Set(['Instrucao', 'Oitivas']);

/** Estados da CONCLUSÃO (anchor apuracaoEncerradaAt, 5 úteis) — após close_oitivas,
 *  antes da decisão. Inclui o intermediário v2 de reabertura. */
const ESTADOS_CONCLUSAO: ReadonlySet<string> = new Set([
  'AvaliacaoEmCurso',
  'AguardandoDecisao',
  'AguardandoRetornoComite',
]);

/** Anchors mínimos para resolver a SLA sem o DenunciaState completo (dashboard). */
export interface PrazoAnchors {
  stateKind: string;
  /** Abertura do caso (estados Aberta/Triagem sem classificar). */
  openedAt: number | null;
  /** Classificação da triagem (início da análise prévia e da apuração). */
  classifiedAt: number | null;
  /** Encerramento da apuração (evento de audit `oitivas_encerradas`) — início da
   *  conclusão. `null` quando indisponível → a janela de conclusão não é avaliada. */
  apuracaoEncerradaAt: number | null;
  /** Emissão da decisão (início do Retorno). */
  decisaoEmittedAt: number | null;
  /** Apuração prorrogada (+30) — vem do audit (evento investigacao_prorrogada). */
  prorrogada: boolean;
}

/**
 * Núcleo PURO compartilhado por detalhe (state completo) e dashboard (anchors
 * projetados). Retorna `null` quando nenhuma SLA se aplica à fase (Encerrada, ou
 * anchor ausente). Mapa estado → janela:
 *   - Triagem: {Aberta, Triagem sem classifiedAt} → openedAt, 5.
 *   - Análise prévia: {Triagem COM classifiedAt} → classifiedAt, 10.
 *   - Apuração: {Instrucao, Oitivas} → classifiedAt, 30 (60 se prorrogada).
 *   - Conclusão: {AvaliacaoEmCurso, AguardandoDecisao, AguardandoRetornoComite} →
 *     apuracaoEncerradaAt, 5.
 *   - Retorno: {Decisao, Recursal} → decisaoEmittedAt, 5.
 */
export function resolverPrazo(
  anchors: PrazoAnchors,
  now: number,
  feriados: Feriados = SEM_FERIADOS,
): PrazoResultado | null {
  const { stateKind } = anchors;

  if (stateKind === 'Aberta' || stateKind === 'Triagem') {
    // Triagem já classificada → conta a ANÁLISE PRÉVIA (classifiedAt, 10). Antes
    // da classificação → conta a TRIAGEM (openedAt, 5).
    if (anchors.classifiedAt !== null) {
      return avaliarPrazo(
        anchors.classifiedAt,
        PRAZO_ANALISE_PREVIA_DIAS,
        now,
        'analise_previa',
        feriados,
      );
    }
    return anchors.openedAt === null
      ? null
      : avaliarPrazo(anchors.openedAt, PRAZO_TRIAGEM_DIAS, now, 'triagem', feriados);
  }

  if (ESTADOS_APURACAO.has(stateKind)) {
    if (anchors.classifiedAt === null) return null;
    const prazo =
      PRAZO_INVESTIGACAO_DIAS + (anchors.prorrogada ? PRAZO_INVESTIGACAO_PRORROGACAO_DIAS : 0);
    return avaliarPrazo(anchors.classifiedAt, prazo, now, 'apuracao', feriados);
  }

  if (ESTADOS_CONCLUSAO.has(stateKind)) {
    return anchors.apuracaoEncerradaAt === null
      ? null
      : avaliarPrazo(anchors.apuracaoEncerradaAt, PRAZO_CONCLUSAO_DIAS, now, 'conclusao', feriados);
  }

  if (stateKind === 'Decisao' || stateKind === 'Recursal') {
    return anchors.decisaoEmittedAt === null
      ? null
      : avaliarPrazo(anchors.decisaoEmittedAt, PRAZO_RETORNO_DIAS, now, 'retorno', feriados);
  }

  return null;
}

/**
 * Resolve o prazo a partir do DenunciaState completo (detalhe do caso). Extrai os
 * anchors do state e delega ao núcleo `resolverPrazo`. `prorrogada` e
 * `apuracaoEncerradaAt` vêm do audit (derivados server-side; defaults neutros) —
 * o state não os carrega. Sem `apuracaoEncerradaAt`, a janela de conclusão fica
 * indisponível (null) para os estados pós-close_oitivas.
 */
export function prazoDoEstado(
  state: DenunciaState,
  now: number,
  prorrogada = false,
  feriados: Feriados = SEM_FERIADOS,
): PrazoResultado | null {
  return resolverPrazo(
    {
      stateKind: state.kind,
      openedAt: 'openedAt' in state ? state.openedAt : null,
      classifiedAt: 'triagem' in state && state.triagem ? state.triagem.classifiedAt : null,
      // Conclusão (Div. 3) — âncora precisa carregada no state pelo close_oitivas.
      apuracaoEncerradaAt:
        'avaliacaoIniciadaAt' in state ? (state.avaliacaoIniciadaAt ?? null) : null,
      decisaoEmittedAt: 'decisao' in state ? state.decisao.emittedAt : null,
      prorrogada,
    },
    now,
    feriados,
  );
}
